// Starter legal text for ShiftReady — NEEDS HEALTHCARE-ATTORNEY REVIEW before public launch.
// Plain-language drafts, written to be easily reviewable.

const EFFECTIVE = 'October 2026';

export const MEDICAL_DISCLAIMER = {
  title: 'Medical Disclaimer',
  sections: [
    {
      heading: 'Not a medical device',
      body:
        'ShiftReady is a personal organizational tool — a digital "brain sheet" — that helps nurses keep notes and reminders during a single shift. It is NOT a medical device, NOT a clinical decision support system, and has NOT been cleared, approved, or registered by the U.S. Food and Drug Administration (FDA), the U.K. MHRA, Health Canada, the TGA, the EMA, or any other regulatory authority.',
    },
    {
      heading: 'Not a substitute for clinical judgment',
      body:
        'All clinical content in the app (lab reference ranges, IV compatibility tables, nursing skill steps, patient phrases, dose calculators) is provided for general educational reference only. It does not replace your hospital\'s protocols, your training, your scope of practice, or your professional clinical judgment. Always verify any reference value against your facility\'s current policies and the manufacturer\'s prescribing information before acting on it.',
    },
    {
      heading: 'No diagnosis or treatment',
      body:
        'Nothing in ShiftReady is intended to diagnose, treat, cure, or prevent any disease or condition. The app does not analyze patient data and does not provide medical recommendations.',
    },
    {
      heading: 'Calculator accuracy',
      body:
        'The dosage, BMI, urine output, and IV drip-rate calculators are provided as a convenience. Outputs may contain rounding errors or be unsuitable for a particular patient. ALWAYS verify any calculation against your hospital\'s policy and an authoritative reference (e.g., Lexicomp, Micromedex, manufacturer label) before administration.',
    },
    {
      heading: 'Patient safety',
      body:
        'You remain solely responsible for the clinical care you provide. If you suspect a discrepancy, error, or safety risk in any information shown by the app, do not rely on it — consult your facility\'s drug information service, the prescriber, or another authoritative source before proceeding.',
    },
    {
      heading: 'Emergency use',
      body:
        'This app is NOT intended for use in a code blue, anaphylaxis, or any other emergency where immediate action is required. In an emergency, follow your facility\'s ACLS / BLS / response protocol and the directions of the responding clinical team.',
    },
  ],
};

export const TERMS_OF_SERVICE = {
  title: 'Terms of Service',
  sections: [
    {
      heading: '1. Acceptance',
      body:
        'By installing, opening, or using ShiftReady ("the App"), you agree to be bound by these Terms of Service. If you do not agree, do not use the App.',
    },
    {
      heading: '2. Who may use the App',
      body:
        'ShiftReady is intended for use by licensed nurses, nursing students under supervision, and other healthcare professionals within their scope of practice. You confirm that you are at least 18 years old and that your use of the App is consistent with your professional licensure and your employer\'s policies on personal-device use.',
    },
    {
      heading: '3. License',
      body:
        'We grant you a personal, revocable, non-exclusive, non-transferable license to use the App on devices you own or control, for your own clinical workflow. You may not resell, sublicense, reverse-engineer with intent to copy, or use the App in any unlawful manner.',
    },
    {
      heading: '4. Your data',
      body:
        'You retain full ownership of any information you enter into the App. We do not receive, store, sell, or analyze the information you enter. It is kept in your device\'s local storage and is designed to be erased after each shift (see the Privacy Policy for details and limits).',
    },
    {
      heading: 'Subscription and billing',
      body:
        'ShiftReady on iPhone and Android requires an auto-renewing monthly subscription (currently US$7.99 per month; the local price is shown in the App before you buy). Eligible new subscribers get a 7-day free trial. Unless you cancel at least 24 hours before the trial or the current period ends, the subscription renews each month and is charged to your Apple ID or Google Play account. You can cancel at any time in your Apple ID or Google Play subscription settings; you keep access until the end of the period you already paid for. Payments, renewals, refunds and billing problems are handled by Apple or Google under their terms. ShiftReady LLC does not receive your payment details and cannot issue refunds directly. If your subscription ends, the App will not let you start a new shift, but you can always end an active shift and erase its data. For iPhone users, these Terms are the App\'s End User License Agreement (EULA); Apple is not responsible for the App or its support.',
    },
    {
      heading: '5. No professional advice',
      body:
        'The App provides reference information and tools to help you organize your work. It does not provide medical, legal, or professional advice (see Medical Disclaimer). You are solely responsible for clinical decisions you make while using the App.',
    },
    {
      heading: '6. Disclaimer of warranties',
      body:
        'THE APP IS PROVIDED "AS IS" AND "AS AVAILABLE", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE APP WILL BE UNINTERRUPTED, ERROR-FREE, OR FREE OF INACCURACIES.',
    },
    {
      heading: '7. Limitation of liability',
      body:
        'TO THE MAXIMUM EXTENT PERMITTED BY LAW, WE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS, DATA, OR GOODWILL, ARISING OUT OF OR IN CONNECTION WITH YOUR USE OF THE APP. OUR TOTAL CUMULATIVE LIABILITY SHALL NOT EXCEED ONE HUNDRED U.S. DOLLARS (USD 100).',
    },
    {
      heading: '8. Indemnification',
      body:
        'You agree to indemnify and hold us harmless from any claim, damage, or expense (including reasonable attorney\'s fees) arising from your use of the App, your violation of these Terms, or your violation of any law or third-party right.',
    },
    {
      heading: '9. Changes',
      body:
        'We may update these Terms from time to time. Continued use after changes constitutes acceptance. Material changes will be flagged on the launch screen.',
    },
    {
      heading: '10. Governing law',
      body:
        'These Terms are governed by the laws of the State of Florida, without regard to conflict-of-laws principles. Disputes shall be resolved exclusively in the state or federal courts located in Florida.',
    },
    {
      heading: '11. Contact',
      body:
        'ShiftReady is operated by ShiftReady LLC, a Florida limited liability company (EIN ending in 1753). Questions about these Terms? Email: support@shiftreadyllc.com.',
    },
  ],
};

export const PRIVACY_POLICY = {
  title: 'Privacy Policy',
  sections: [
    {
      heading: 'Who we are',
      body:
        'ShiftReady is operated by ShiftReady LLC, a Florida limited liability company (EIN ending in 1753). Contact: support@shiftreadyllc.com.',
    },
    {
      heading: 'Plain-English summary',
      body:
        'ShiftReady LLC does not run servers that receive or store your shift data. Your patient notes, SBARs and markers are saved in your device\'s local storage and are designed to erase automatically after your shift. Some device features you choose to use, such as voice dictation, are provided by Apple, Google or your browser and are covered by their privacy policies. Details below.',
    },
    {
      heading: '1. Information we collect',
      body:
        'ShiftReady LLC does not operate accounts, a database or analytics for the App, and we do not receive the information you enter into it. Like any website, the web version is delivered by a hosting provider whose servers may automatically log technical request data (such as IP address, browser type and time of request) when the App\'s files are downloaded. The hosting network may also record basic, aggregate page-load metrics (for example via a Cloudflare performance beacon). These logs and metrics do not contain the notes you type into the App, and we do not use them to identify you. App stores (Apple, Google) may collect download and diagnostic data under their own policies.',
    },
    {
      heading: '2. Where your data is stored',
      body:
        'Information you enter (patient initials, room numbers, vitals, diagnosis, notes, SBARs, voice recordings, alarms, body diagram markers, etc.) is stored in your device\'s local storage: browser storage on the web, app storage on iPhone and Android. We have no access to it. The mobile apps ask the operating system to exclude this data from iCloud and Android cloud backups. Information you choose to copy, share, email or dictate leaves the App and is handled by the app or service you send it to.',
    },
    {
      heading: '3. How long we keep your data',
      body:
        'We do not receive your shift data, so we do not keep it. On your device, shift data is designed to be erased when: (a) you tap "End Shift"; (b) the idle timeout passes with no PIN set; (c) 2 hours have passed after your scheduled shift end; (d) 5 wrong PIN attempts are entered; or (e) you uninstall the App or clear its site data. Time-based erasure runs while the App is open, or the next time it is opened; if the App is never reopened, data remains in your device\'s storage until the App is opened, uninstalled or its data is cleared. Copies made outside the App (screenshots, shared or emailed text, or full-device backups made by device-management software) are not covered.',
    },
    {
      heading: '4. Cookies / tracking',
      body:
        'The App itself contains no cookies, advertising IDs, analytics or third-party tracking code (aside from the hosting-network page-load metrics described in section 1). The App uses browser- and device-native storage (localStorage, IndexedDB, Cache Storage, app storage, Keychain/Keystore) only so it can work, including offline.',
    },
    {
      heading: '5. Third parties',
      body:
        'Voice dictation: when you tap a microphone button in the web App, your browser\'s speech-recognition service converts your speech to text. On many browsers, including Google Chrome and Apple Safari, the audio is sent to the browser maker\'s servers (Google or Apple) for processing and is handled under their privacy policies, not ours. The App shows a warning before your first dictation. Do not dictate patient names, dates of birth, medical record numbers or other direct identifiers. The same applies to the microphone key on your phone\'s keyboard. Read-aloud and alarms use your device\'s built-in speech and audio features. If you use "Email", "Share" or "Copy", the data is handed to the app or recipient you choose.',
    },
    {
      heading: 'Subscriptions and payments',
      body:
        'Subscriptions are bought and processed by Apple (App Store) or Google (Google Play). The store tells the App on your device whether your subscription is active; the App saves only that status and its expiry date on your device so it can work offline. ShiftReady LLC does not receive your name, payment details or purchase history, and we do not run a server that checks your subscription.',
    },
    {
      heading: 'HIPAA and your employer',
      body:
        'ShiftReady LLC does not receive, store or transmit Protected Health Information (PHI) on your behalf. Whether you may use a personal device, and this App, for patient information is decided by your employer\'s policies and applicable law. Check with your facility\'s privacy officer before use. Nothing in this policy is legal advice or a guarantee of regulatory compliance.',
    },
    {
      heading: '6. Children',
      body:
        'ShiftReady is intended for licensed healthcare professionals and is not directed to children under 13. We do not knowingly collect any information from anyone.',
    },
    {
      heading: '7. Security',
      body:
        'The main risk to data stored on your device is unauthorized local access. We reduce this risk with a required PIN (PBKDF2-hashed), optional biometric lock, a 5-attempt brute-force wipe, an idle-timeout lock and a post-shift expiry wipe. No security measure is perfect, and we cannot guarantee that data on a lost, shared, compromised or employer-managed device will never be accessed. You are responsible for keeping your device itself locked (OS-level PIN / Face ID / fingerprint).',
    },
    {
      heading: '8. Your rights',
      body:
        'Because we do not store your data, there is no data for us to provide, correct, or delete on your request. You can delete all App data at any time by tapping "End Shift" or clearing your browser\'s site data.',
    },
    {
      heading: '9. International users',
      body:
        'The App runs on your device regardless of your location. We do not receive the shift data you enter, so we do not transfer it across borders. Third-party services you choose to use, such as voice dictation, may process data in other countries. Local laws may impose additional obligations on you as a clinician using a personal device for work.',
    },
    {
      heading: '10. Changes',
      body:
        'If we ever change this policy to involve any data collection, we will require explicit re-consent before activating that change.',
    },
    {
      heading: '11. Contact',
      body:
        'ShiftReady is operated by ShiftReady LLC, a Florida limited liability company (EIN ending in 1753). Privacy questions? Email: support@shiftreadyllc.com.',
    },
  ],
};

export const LEGAL_EFFECTIVE_DATE = EFFECTIVE;
// Bump this integer to force every device to re-acknowledge the three legal
// documents on next launch. Material changes that require re-ack:
//   v1 → v2: added ShiftReady LLC (Florida) as named operator in Terms & Privacy.
//   v2 → v3: rewrote privacy claims; disclosed dictation, hosting logs and erase limits.
//   v3 → v4: added subscription/billing terms and payments privacy section.
export const LEGAL_VERSION = 4;
